"use client";

import { Menu, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { Button } from "./Button";
import { Logo } from "./Logo";
import { cn } from "@/lib/utils";

const navLinks = [
  { href: "/#features", label: "Features" },
  { href: "/#preview", label: "Product" },
  { href: "/#how-it-works", label: "How It Works" },
  { href: "/cli", label: "CLI", highlight: false },
  { href: "/blog", label: "Blog" },
];

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hash, setHash] = useState("");
  const pathname = usePathname();

  useEffect(() => {
    // Set initial hash
    setHash(window.location.hash);

    // Listen for hash changes
    const handleHashChange = () => {
      setHash(window.location.hash);
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const handleNavClick = (href: string) => {
    // Update hash state immediately for hash links
    if (href.startsWith("/#")) {
      const newHash = href.substring(1);
      setHash(newHash);
    }
  };

  const isActiveLink = (href: string) => {
    // For blog pages
    if (href === "/blog") {
      return pathname === "/blog" || pathname.startsWith("/blog/");
    }
    // For CLI pages
    if (href === "/cli") {
      return pathname === "/cli" || pathname.startsWith("/cli/");
    }
    // For hash links on home page
    if (href.startsWith("/#") && pathname === "/") {
      const linkHash = href.substring(1); // Remove leading "/"
      return hash === linkHash;
    }
    return pathname === href;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 h-[64px] md:h-[72px] flex items-center bg-white/95 backdrop-blur-[20px] border-b border-border z-[1000]">
      <div className="max-w-[1400px] w-full mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <Link
          href="/"
          className="flex items-center gap-2 md:gap-3 no-underline text-text"
        >
          <Logo className="w-8 h-8 md:w-9 md:h-9" />
          <span className="font-bold text-lg md:text-xl tracking-tight">
            Specledger
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-1 xl:gap-2">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              onClick={() => handleNavClick(link.href)}
              className={cn(
                "no-underline text-sm font-medium px-3 xl:px-4 py-2 rounded-lg transition-all duration-200",
                link.highlight
                  ? "font-semibold hover:opacity-80"
                  : "text-text-muted hover:text-text",
                isActiveLink(link.href)
                  ? "bg-bg-alt"
                  : "hover:bg-bg-alt",
                isActiveLink(link.href) && !link.highlight && "text-text"
              )}
            >
              {link.highlight ? (
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  {link.label}
                </span>
              ) : (
                link.label
              )}
            </Link>
          ))}
        </div>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center gap-2 lg:gap-3">
          <Button variant="primary" href="https://app.specledger.io/login">
            Get Started
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="lg:hidden p-2 -mr-2 text-text-muted hover:text-text transition-colors"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div className="lg:hidden absolute top-[64px] left-0 right-0 bg-white border-b border-border shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                onClick={() => {
                  handleNavClick(link.href);
                  setIsMenuOpen(false);
                }}
                className={cn(
                  "block no-underline text-base font-medium px-4 py-3 rounded-lg transition-all duration-200",
                  link.highlight
                    ? "font-semibold"
                    : "text-text-muted hover:text-text",
                  isActiveLink(link.href)
                    ? "bg-bg-alt"
                    : "hover:bg-bg-alt",
                  isActiveLink(link.href) && !link.highlight && "text-text"
                )}
              >
                {link.highlight ? (
                  <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    {link.label}
                  </span>
                ) : (
                  link.label
                )}
              </Link>
            ))}
            <div className="pt-4 mt-4 border-t border-border space-y-2">
              <Button
                variant="primary"
                href="https://app.specledger.io/login"
                className="w-full justify-center"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
